import { create } from 'zustand';
import { api, ApiError, mapOperatorAccount } from '../api';
import { getTelegramInitData } from '../lib/telegram';
import { Operator } from '../types';

interface AuthState {
  isAuthenticated: boolean;
  isBootstrapping: boolean;
  isLoading: boolean;
  loginError: string | null;
  operator: Operator | null;

  initialize: () => Promise<void>;
  login: (login: string, password: string) => Promise<boolean>;
  loginWithTelegram: (initData?: string) => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
  setOperator: (operator: Operator | null) => void;
}

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Не удалось выполнить запрос к backend.';
}

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: false,
  isBootstrapping: true,
  isLoading: false,
  loginError: null,
  operator: null,

  initialize: async () => {
    set({ isBootstrapping: true, loginError: null });

    try {
      const currentOperator = await api.getCurrentOperator();
      set({
        isAuthenticated: true,
        operator: mapOperatorAccount(currentOperator),
        isBootstrapping: false,
      });
      return;
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 401) {
        set({ isAuthenticated: false, operator: null, isBootstrapping: false });
        return;
      }
    }

    try {
      const refreshed = await api.refreshSession();
      set({
        isAuthenticated: true,
        operator: mapOperatorAccount(refreshed.operator),
        isBootstrapping: false,
      });
    } catch {
      set({
        isAuthenticated: false,
        operator: null,
        isBootstrapping: false,
      });
    }
  },

  login: async (login: string, password: string) => {
    set({ isLoading: true, loginError: null });

    try {
      const session = await api.loginWithPassword(login.trim(), password);
      set({
        isAuthenticated: true,
        operator: mapOperatorAccount(session.operator),
        isLoading: false,
        loginError: null,
      });
      return true;
    } catch (error) {
      set({
        isAuthenticated: false,
        operator: null,
        isLoading: false,
        loginError: toErrorMessage(error),
      });
      return false;
    }
  },

  loginWithTelegram: async (initData?: string) => {
    const rawInitData = (initData ?? getTelegramInitData()).trim();

    if (rawInitData.length < 20) {
      set({ loginError: 'Telegram Mini App initData не найден.', isLoading: false });
      return false;
    }

    set({ isLoading: true, loginError: null });

    try {
      const session = await api.loginWithTelegram(rawInitData);
      set({
        isAuthenticated: true,
        operator: mapOperatorAccount(session.operator),
        isLoading: false,
        loginError: null,
      });
      return true;
    } catch (error) {
      set({
        isAuthenticated: false,
        operator: null,
        isLoading: false,
        loginError: toErrorMessage(error),
      });
      return false;
    }
  },

  logout: async () => {
    try {
      await api.logout();
    } finally {
      set({
        isAuthenticated: false,
        operator: null,
        loginError: null,
        isLoading: false,
      });
    }
  },

  clearError: () => set({ loginError: null }),
  setOperator: (operator) => set({ operator, isAuthenticated: !!operator }),
}));
